import { useCallback, useState } from 'react';

import { cfetch } from '@confluence/network';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { buildExternalLabelsAri } from './useExternalLabels.ts';

export type ManagedLabel = {
	id: string;
	kind: string;
	name: string;
};

type UseManagedLabelMutationsResult = {
	removeManagedLabel: (labelId: string, labelName: string) => Promise<boolean>;
	removeError: Error | null;
};

/**
 * Provides the mutation (un-apply) side of managed (external) labels.
 *
 * The READ side has moved to the Relay `useExternalLabels` hook (AGG
 * `confluence_externalLabels`). This hook now owns only the removal flow, which
 * is still a hand-rolled `cfetch` mutation.
 *
 * The `tags_unassignLabel` mutation resolves on the gateway but is hidden from
 * the synced SDL (like the read field was before it was exposed), so it can't be
 * compiled by relay-compiler yet. The label `kind` is `managed-label` (confirmed
 * against real network responses on the staging tenant).
 *
 * TODO(DUKE-3005): convert removal to Relay `useMutationInterop` once the
 * mutation is published to the AGG SDL.
 *
 * Gated behind `confluence_managed_external_labels_endpoint` (enforced upstream
 * by the section render and the read hook).
 */
export const useManagedLabels = (contentId: string): UseManagedLabelMutationsResult => {
	const { cloudId, orgId } = useSessionData();
	const [removeError, setRemoveError] = useState<Error | null>(null);

	// The page ARI used to identify the content when removing labels.
	const entityAri = buildExternalLabelsAri(cloudId, contentId);
	// CDAS expects an org ARI, not a plain org ID.
	const orgAri = orgId ? `ari:cloud:platform::org/${orgId}` : '';
	const workspaceAri = cloudId ? `ari:cloud:confluence:${cloudId}:site/${cloudId}` : '';

	const removeManagedLabel = useCallback(
		async (labelId: string, _labelName: string): Promise<boolean> => {
			if (!contentId || !entityAri || !orgAri || !workspaceAri) {
				return false;
			}

			setRemoveError(null);

			try {
				const response = await cfetch('/gateway/api/graphql', {
					method: 'POST',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({
						query: `mutation managedUnassignLabel($kind: String!, $input: TagsUnassignLabelInput!) {
							tags_unassignLabel(kind: $kind, input: $input)
						}`,
						variables: {
							kind: 'managed-label',
							input: {
								entityAri,
								labelId,
								orgId: orgAri,
								workspaceAri,
							},
						},
					}),
				});

				if (!response.ok) {
					throw new Error(`Failed to remove managed label: ${response.status}`);
				}

				const result = await response.json();

				if (result.errors?.length) {
					throw new Error(result.errors[0].message);
				}

				return true;
			} catch (err) {
				setRemoveError(err instanceof Error ? err : new Error(String(err)));
				return false;
			}
		},
		[contentId, entityAri, orgAri, workspaceAri],
	);

	return {
		removeManagedLabel,
		removeError,
	};
};
